<script setup lang="ts">
import { useNuxtApp } from '#app'
import { ref } from 'vue'
import HeaderPage   from '~/pages/header.vue'
import MenuPage     from '~/pages/menu.vue'
import CarouselPage from '~/pages/carousel.vue'
import CartPage     from '~/pages/cart.vue'
import { useVmStore } from '@/stores/vm'
import ShoppingPanel from '~/components/ShoppingList.vue'
import { ranaSignInMarket } from '@monorepo/ui'
import IconCanvas from '~/assets/icons/IconCanvas.vue'
import IconDiscover from '~/assets/icons/IconDiscover.vue'
import IconCart from '~/assets/icons/IconCart.vue'
import { useShoppingLists } from '@/composables/shoppingLists'  // <<— import the composable
import {loadInitialAuthData} from "../utils/jwtToken.js"

// grab your VM store as before
const vm = useVmStore()

// fire off the shopping-lists composable (runs its onMounted hydrate logic)
useShoppingLists()

// your existing layout logic…
const activeTab = ref<'menu'|'main'|'cart'>('main')
const setActiveTab = (tab: typeof activeTab.value) => activeTab.value = tab
const isTabletView = ref(false)
const checkView = () => { isTabletView.value = window.innerWidth >= 768 && window.innerWidth <= 1024 }

const openShoppingPanel = () => {
  const { $vm } = useNuxtApp()
  $vm.openRightPanel(ShoppingPanel)
}
const openLoginPanel = () => {
  const { $vm } = useNuxtApp()
  $vm.openRightPanel(ranaSignInMarket)
}
const handlePanelClose = () => {

  vm.closeRightPanel()
}

const handlePanelCloseWithLogin = () => {
  const token = localStorage.getItem('auth_token') || ''
  if (token.length > 10) vm.user.jwtToken = token

    const { token: initialJwtToken, expiry: initialJwtTokenExpiry, countryCode: initialJwtCountryCode, email: initialUserEmail } = loadInitialAuthData()
    vm.user.email=initialUserEmail
    vm.user.jwtToken= initialJwtToken
    vm.user.loginExpires= initialJwtTokenExpiry
    vm.user.countryCode= initialJwtCountryCode 
  
  vm.closeRightPanel()
}

const showMenu = computed(() => {
  if (isTabletView.value) return !vm.isRightPanelOpen
  return true
})

const setupResizeListener = () => {
  checkView()
  window.addEventListener('resize', checkView)
}

onMounted(() => {
  setupResizeListener()
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkView)
})
</script>


<template>
  <div class="flex flex-col h-screen">
    <!-- fixed header -->
    <HeaderPage @open-login-panel="openLoginPanel" />

    <!-- Desktop Layout -->
    <div class="hidden md:flex flex-1" v-if="!vm.isMobile">
      <!-- left menu -->
      <MenuPage v-if="showMenu"/>

      <!-- main content -->
      <div class="flex flex-col flex-1 overflow-hidden">
        <div
          v-if="vm.showCarousel"
          class="w-full px-4"
          :style="{ height: vm.carouselHeight }"
        >
          <CarouselPage :slides="vm.carouselProducts" display-mode="landscape"/>
        </div>
        <main
          class="flex-1 overflow-y-auto highlight-scrollbar
                 bg-[hsl(var(--background))] text-[hsl(var(--content))]"
        >
          <slot />
        </main>
      </div>

      <!-- right column: panel + cart -->
      <div
        class="flex flex-col overflow-hidden transition-all duration-300 h-[calc(100vh-184px)]"
         :class="vm.isRightPanelOpen ? 'w-72 pt-2 pb-2' : 'w-0 p-0'"
      >
        <!-- dynamic slide-in panel -->
        <section
          v-show="vm.isRightPanelOpen && vm.rightPanelComponent"
          class="flex-1 overflow-y-auto bg-neutral rounded-lg shadow-lg p-2"
        >
          <component :is="vm.rightPanelComponent"  @close="handlePanelClose" @closeLogin="handlePanelCloseWithLogin"/>
        </section>

        <!-- cart at bottom -->
        <div class="mt-auto mr-0">
          <CartPage @open-shopping-panel="openShoppingPanel" />
        </div>
      </div>
    </div>

    <!-- Mobile Layout with Tabs -->
    <div class="flex flex-col md:hidden flex-1" v-if="vm.isMobile">
      <!-- Tab Content -->
      <div class="flex-1 overflow-hidden">
        <!-- Menu Tab Content -->
        <div v-show="activeTab === 'menu'" class="h-full overflow-y-auto">
          <MenuPage />
        </div>

        <!-- Main Content Tab -->
        <div v-show="activeTab === 'main'" class="h-full flex flex-col">
          <div
            v-if="vm.showCarousel"
            class="w-full px-4"
            :style="{ height: vm.carouselHeight }"
          >
            <CarouselPage :slides="vm.carouselProducts" display-mode="landscape"/>
          </div>
          <main
            class="flex-1 overflow-y-auto highlight-scrollbar
                  bg-[hsl(var(--background))] text-[hsl(var(--content))]"
          >
            <slot />
          </main>
        </div>

        
      <!-- right column: panel + cart -->
     <div v-show="activeTab === 'cart'" class="h-full overflow-y-auto">
          <div v-if="vm.isRightPanelOpen && vm.rightPanelComponent" class="mb-4">
            <component :is="vm.rightPanelComponent" />
          </div>
          <CartPage />
        </div>
      </div>

      <!-- Bottom Tab Navigation -->
      <div class="bg-neutral h-16 -mt-15 z-50 flex items-center justify-around border-t border-[#00ccaa]/30 shadow-lg">
        <!-- Menu Tab -->
        <button 
          @click="setActiveTab('menu')"
          class="flex flex-col items-center justify-center w-1/3 h-full"
          :class="activeTab === 'menu' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
        >
          <IconDiscover class="h-8 w-8"/>
          <span class="text-xs mt-1">Menu</span>
        </button>

        <!-- Main Content Tab -->
        <button 
          @click="setActiveTab('main')"
          class="flex flex-col items-center justify-center w-1/3 h-full"
          :class="activeTab === 'main' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
        >
          <IconCanvas class="h-8 w-8"/>
          <span class="text-xs mt-1">Home</span>
        </button>

        <!-- Cart Tab -->
            
        <button 
          @click="setActiveTab('cart'); vm.isRightPanelOpen ? vm.closeRightPanel() : openShoppingPanel();"
          class="flex flex-col items-center justify-center w-1/3 h-full"
          :class="activeTab === 'cart' ? 'text-[#64F4D5] border-t-2 border-[#64F4D5]' : 'text-[#00ccaa]'"
        >
          <IconCart class="h-9 w-9"/>
          <span class="text-xs mt-1">Cart</span>
        </button>
      </div>
    </div>
  </div>
</template>